import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Divider,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { getUrlError, ALLOWED_DOMAINS_DISPLAY, isSameUrl } from "./redirectConfig";

const defaultForm = {
  from_path: "",
  to_url: "",
  status_code: "none",
  is_active: true,
};

export default function AddRedirectModal({ open, onClose, onSave, editData, loading }) {
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editData) {
      setForm({
        from_path: editData.from_path || "",
        to_url: editData.to_url || "",
        status_code: editData.status_code || "none",
        is_active: editData.is_active ?? true,
      });
    } else {
      setForm(defaultForm);
    }
    setErrors({});
  }, [editData, open]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const errs = {};

    const fromError = getUrlError(form.from_path.trim(), "From URL");
    if (fromError) errs.from_path = fromError;

    const toError = getUrlError(form.to_url.trim(), "Redirect to URL");
    if (toError) errs.to_url = toError;

    // ✅ Same URL check
    if (!errs.from_path && !errs.to_url && isSameUrl(form.from_path, form.to_url)) {
      errs.to_url = '"From" and "To" URLs cannot be the same';
    }

    if (form.status_code === "none") {
      errs.status_code = "Please select a redirect type";
    }

    return errs;
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    const payload = {
      ...form,
      status_code: Number(form.status_code),
    };
    onSave(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          fontWeight: 600,
          fontSize: "1rem",
        }}
      >
        {editData ? "Edit redirect" : "Add redirect"}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        {/* Preview */}
        {(form.from_path || form.to_url) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2.5,
              p: 1.5,
              backgroundColor: "#f0f7ff",
              borderRadius: 1.5,
              border: "1px solid #bbdefb",
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: "#1565c0" }}>
              {form.from_path || "…"}
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 16, color: "#9e9e9e", flexShrink: 0 }} />
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: 13,
                color: "#2e7d32",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 260,
              }}
            >
              {form.to_url || "…"}
            </Typography>
          </Box>
        )}

        <TextField
          label="From URL"
          placeholder="https://obenelectric.com/old-page"
          fullWidth
          size="small"
          value={form.from_path}
          onChange={set("from_path")}
          error={!!errors.from_path}
          helperText={errors.from_path || `Must use: ${ALLOWED_DOMAINS_DISPLAY}`}
          sx={{ mb: 2.5 }}
          InputProps={{ sx: { fontFamily: "monospace", fontSize: 14 } }}
        />

        <TextField
          label="Redirect to URL"
          placeholder="https://obenelectric.com/new-page"
          fullWidth
          size="small"
          value={form.to_url}
          onChange={set("to_url")}
          error={!!errors.to_url}
          helperText={errors.to_url || `Must use: ${ALLOWED_DOMAINS_DISPLAY}`}
          sx={{ mb: 2.5 }}
          InputProps={{ sx: { fontFamily: "monospace", fontSize: 14 } }}
        />

        <FormControl
          fullWidth
          size="small"
          sx={{ mb: 2.5 }}
          error={!!errors.status_code}
        >
          <InputLabel>Redirect type *</InputLabel>
          <Select
            value={form.status_code}
            onChange={(e) => {
              setForm((f) => ({ ...f, status_code: e.target.value }));
              if (errors.status_code) setErrors((e) => ({ ...e, status_code: "" }));
            }}
            label="Redirect type *"
          >
            <MenuItem value="none" disabled>Select type</MenuItem>
            <MenuItem value={301}>
              <Box>
                <Typography fontSize={14}>301 — Permanent redirect</Typography>
                <Typography fontSize={12} color="text.secondary">
                  Browser & SEO caches this permanently
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem value={302}>
              <Box>
                <Typography fontSize={14}>302 — Temporary redirect</Typography>
                <Typography fontSize={12} color="text.secondary">
                  Not cached; good for seasonal or A/B pages
                </Typography>
              </Box>
            </MenuItem>
          </Select>
          {errors.status_code && (
            <Typography fontSize={11} color="error" mt={0.5}>
              {errors.status_code}
            </Typography>
          )}
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#1976d2" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#1976d2",
                },
              }}
            />
          }
          label={
            <Box>
              <Typography fontSize={14} fontWeight={500}>
                {form.is_active ? "Active" : "Inactive"}
              </Typography>
              <Typography fontSize={12} color="text.secondary">
                {form.is_active
                  ? "Redirect will be applied to incoming traffic"
                  : "Redirect is saved but will not be applied"}
              </Typography>
            </Box>
          }
        />
      </DialogContent>

      <Divider sx={{ mt: 1 }} />
      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: "none", borderColor: "#bdbdbd", color: "#424242" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            textTransform: "none",
            backgroundColor: "#1976d2",
            "&:hover": { backgroundColor: "#1565c0" },
            minWidth: 100,
          }}
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : editData ? "Save changes" : "Add redirect"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}